import { Box, Stack, useMediaQuery } from '@mui/material';
import { FC, PropsWithChildren, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getNightOrDayPalette } from '../utils/getNightOrDayPalette';
import { useHSMContext } from './HorizontalScrollMapContext';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material';
import { SummaryBanner } from './summary/SummaryBanner';
import { useDrawerContext } from '../drawer/DrawerContext';

const WIDGETS_COLUMN_WIDTH = 209;

export const DetailContentScrollbar: FC<PropsWithChildren> = ({ children }) => {
	const contentRef = useRef<HTMLDivElement>(null);
	const scrollTrackHorizontalRef = useRef<HTMLDivElement>(null);
	const scrollThumbHorizontalRef = useRef<HTMLDivElement>(null);
	const scrollTrackVerticalRef = useRef<HTMLDivElement>(null);
	const scrollThumbVerticalRef = useRef<HTMLDivElement>(null);
	const fixedScrollHeight = useRef<number>(0);
	const { content, isVerticalScrollEnabled } = useHSMContext();
	const { setDrawerWithoutPrevParams } = useDrawerContext();

	// Initial setup of scroll thumbs
	const [horizontalThumbWidth, setHorizontalThumbWidth] = useState(20);
	const [verticalThumbHeight, setVerticalThumbHeight] = useState(20);

	// Mouse scrolling related states
	const [isHorizontalDragging, setIsHorizontalDragging] = useState(false);
	const [isVerticalDragging, setIsVerticalDragging] = useState(false);
	const [scrollHorizontalStartPosition, setScrollHorizontalStartPosition] = useState(0);
	const [scrollVerticalStartPosition, setScrollVerticalStartPosition] = useState(0);
	const [initialContentTopStart, setInitialContentTopStart] = useState(0); // initial vertical scroll position of page content
	const [initialContentLeftStart, setInitialContentLeftStart] = useState(0); // initial horizontal scroll position of page content

	// Scrollbar visibility
	const [showVerticalScrollbar, setShowVerticalScrollbar] = useState(false);
	const [showHorizontalScrollbar, setShowHorizontalScrollbar] = useState(false);
	const timeoutRef = useRef<number>(null);

	const { breakpoints } = useTheme();
	const isTabletOrSmaller = useMediaQuery(() => breakpoints.down('md'));
	const [searchParams] = useSearchParams();

	const handleResize = () => {
		if (scrollTrackHorizontalRef.current && contentRef.current) {
			const { clientWidth: trackWidth } = scrollTrackHorizontalRef.current;
			const { clientWidth: contentVisible, scrollWidth: contentTotalWidth } = contentRef.current;

			setHorizontalThumbWidth(Math.max((contentVisible / contentTotalWidth) * trackWidth, 20));
		}
		if (scrollTrackVerticalRef.current && contentRef.current) {
			const { clientHeight: trackHeight } = scrollTrackVerticalRef.current;
			const { clientHeight: contentVisible, scrollHeight: contentTotalHeight } = contentRef.current;

			setVerticalThumbHeight(Math.max((contentVisible / contentTotalHeight) * trackHeight, 20));
		}
		checkIfHorizontalScrollNecessary();
	};

	const handleVerticalThumbPosition = () => {
		if (!contentRef.current || !scrollTrackVerticalRef.current || !scrollThumbVerticalRef.current) {
			return;
		}

		const { scrollTop: contentTop, scrollHeight: contentHeight } = contentRef.current;
		const { scrollHeight: trackHeight } = scrollTrackVerticalRef.current;

		let newTop = (contentTop / contentHeight) * trackHeight;
		newTop = Math.min(newTop, trackHeight - verticalThumbHeight);

		const thumb = scrollThumbVerticalRef.current;
		requestAnimationFrame(() => (thumb.style.top = `${newTop}px`));
	};

	const handleHorizontalThumbPosition = () => {
		if (!contentRef.current || !scrollTrackHorizontalRef.current || !scrollThumbHorizontalRef.current) {
			return;
		}

		const { scrollLeft: contentLeft, scrollWidth: contentWidth } = contentRef.current;
		const { scrollWidth: trackWidth } = scrollTrackHorizontalRef.current;

		let newLeft = (contentLeft / contentWidth) * trackWidth;
		newLeft = Math.min(newLeft, trackWidth - horizontalThumbWidth);

		const thumb = scrollThumbHorizontalRef.current;
		requestAnimationFrame(() => (thumb.style.left = `${newLeft}px`));
	};

	const handleScroll = () => {
		handleHorizontalThumbPosition();

		if (!isVerticalScrollEnabled) {
			preventVerticalScroll();
		} else {
			handleVerticalThumbPosition();
			handleShowVerticalScrollbar();
		}
	};

	const handleHorizontalThumbDown = (e: React.MouseEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setScrollHorizontalStartPosition(e.clientX);
		if (contentRef.current) {
			setInitialContentLeftStart(contentRef.current.scrollLeft);
		}
		setIsHorizontalDragging(true);
	};

	const handleVerticalThumbDown = (e: React.MouseEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setScrollVerticalStartPosition(e.clientY);
		if (contentRef.current) {
			setInitialContentTopStart(contentRef.current.scrollTop);
		}
		setIsVerticalDragging(true);
	};

	const handleMouseUp = () => {
		if (isVerticalDragging) {
			setIsVerticalDragging(false);
		} else if (isHorizontalDragging) {
			setIsHorizontalDragging(false);
		}
	};

	const handleMouseMove = (e: MouseEvent) => {
		if (contentRef.current) {
			handleContentEdge(e);

			if (isVerticalDragging) {
				const { scrollHeight: contentScrollHeight, clientHeight: contentClientHeight } = contentRef.current;

				const deltaY = (e.clientY - scrollVerticalStartPosition) * (contentClientHeight / verticalThumbHeight);

				const newScrollTop = Math.min(initialContentTopStart + deltaY, contentScrollHeight - contentClientHeight);

				contentRef.current.scrollTop = newScrollTop;
			} else if (isHorizontalDragging) {
				const { scrollWidth: contentScrollWidth, clientWidth: contentClientWidth } = contentRef.current;

				const deltaX = (e.clientX - scrollHorizontalStartPosition) * (contentClientWidth / horizontalThumbWidth);

				const newScrollLeft = Math.min(initialContentLeftStart + deltaX, contentScrollWidth - contentClientWidth);

				contentRef.current.scrollLeft = newScrollLeft;
			}
		}
	};

	const handleVerticalTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		if (scrollTrackVerticalRef.current && contentRef.current) {
			const rect = (e.target as HTMLDivElement).getBoundingClientRect();
			const trackTop = rect.top;
			const clickRatio = (e.clientY - trackTop) / scrollTrackVerticalRef.current.clientHeight;
			const scrollAmount = Math.floor(clickRatio * contentRef.current.scrollHeight);
			contentRef.current.scrollTo({
				top: scrollAmount,
				behavior: 'smooth',
			});
		}
	};

	const handleHorizontalTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		if (scrollTrackHorizontalRef.current && contentRef.current) {
			const rect = (e.target as HTMLDivElement).getBoundingClientRect();
			const trackLeft = rect.left;
			const clickRatio = (e.clientX - trackLeft) / scrollTrackHorizontalRef.current.clientWidth;
			const scrollAmount = Math.floor(clickRatio * contentRef.current.scrollWidth);
			contentRef.current.scrollTo({
				left: scrollAmount,
				behavior: 'smooth',
			});
		}
	};

	const handleShowVerticalScrollbar = () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			//@ts-expect-error
			timeoutRef.current = null;
		}

		if (contentRef.current == null) {
			return;
		}

		const scrollHeight = contentRef.current.scrollHeight;
		const contentHeight = contentRef.current.clientHeight;

		if (Math.abs(scrollHeight - contentHeight) < 5) {
			setShowVerticalScrollbar(false);
		} else {
			setShowVerticalScrollbar(true);
			//@ts-expect-error
			timeoutRef.current = setTimeout(() => setShowVerticalScrollbar(false), 2500);
		}
	};

	const handleContentEdge = (e: MouseEvent) => {
		if (contentRef.current) {
			const contentWidth = contentRef.current.clientWidth;
			const mouseX = e.clientX;

			if (contentWidth - mouseX <= 20 && !showVerticalScrollbar) {
				handleShowVerticalScrollbar();

				// prevent scrollbar from hiding when dragging the vertical thumb or timeout is in progress
			} else if (contentWidth - mouseX > 20 && !isVerticalDragging && timeoutRef.current == null) {
				setShowVerticalScrollbar(false);
			}
		}
	};

	const checkIfHorizontalScrollNecessary = () => {
		if (contentRef.current) {
			const { scrollWidth, clientWidth } = contentRef.current;
			const isNotHorizontallyScrollable = scrollWidth === clientWidth;

			setShowHorizontalScrollbar(!isNotHorizontallyScrollable);
		}
	};

	const preventVerticalScroll = () => {
		if (contentRef.current) {
			contentRef.current.scrollTop = fixedScrollHeight.current;
		}
	};

	useEffect(() => {
		if (contentRef.current) {
			const content = contentRef.current;

			const observer = new MutationObserver(() => handleResize());
			const resizeObserver = new ResizeObserver(() => handleResize());
			observer.observe(content, { subtree: true, childList: true });
			resizeObserver.observe(content);
			content.addEventListener('scroll', handleScroll);

			return () => {
				observer.disconnect();
				resizeObserver.unobserve(content);
				content.removeEventListener('scroll', handleScroll);
			};
		}
	}); // when dep array is missing the scrollbars react almost instantly to changes of content and viewport

	useEffect(() => {
		document.addEventListener('mouseup', handleMouseUp);
		document.addEventListener('mousemove', handleMouseMove);

		return () => {
			document.removeEventListener('mouseup', handleMouseUp);
			document.removeEventListener('mousemove', handleMouseMove);
		};
	});

	useEffect(() => {
		handleScroll();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useLayoutEffect(() => {
		setTimeout(() => {
			// since the current implementation of scroll to activity is only for detail page, we do not need to do filter out URLs
			if (contentRef.current && searchParams.has('activity')) {
				const activityId = searchParams.get('activity');

				if (activityId) {
					const activityElement = document.querySelector(`[data-activity-id="${activityId}"]`);
					const contentMetricId = searchParams.get('content_metric_id');

					if (activityElement) {
						contentRef.current.scroll({
							left: (activityElement as HTMLElement).offsetLeft - (isTabletOrSmaller ? 0 : WIDGETS_COLUMN_WIDTH),
							behavior: 'smooth',
						});
					}

					if (contentMetricId) {
						setDrawerWithoutPrevParams('editMetricWidget', { contentMetricId, activityId });
					}
				} else {
					contentRef.current.scroll({ top: 0 });
				}
			}
		}, 1000);
	}, [isTabletOrSmaller, searchParams, setDrawerWithoutPrevParams]);

	useEffect(() => {
		if (!isVerticalScrollEnabled) {
			fixedScrollHeight.current = contentRef.current?.scrollTop ?? 0;
		}
	}, [isVerticalScrollEnabled]);

	return (
		<Box
			flex={1}
			overflow={'hidden'}
			position={'relative'}
			sx={{
				display: 'grid',
				gridTemplateColumns: `repeat(2, 1fr) 0`,
				gridTemplateRows: `repeat(2, 1fr) ${showHorizontalScrollbar ? '16px' : 0}`,
				transition: 'all 0.15s ease-in-out',
			}}
		>
			<Box
				height={'100%'}
				ref={contentRef}
				gridArea={'1 / 1 / 3 / 3'}
				sx={{
					scrollbarWidth: 'none',
					'::-webkit-scrollbar': {
						display: 'none',
					},
					'-ms-overflow-style': 'none',
					overflow: 'auto',
				}}
			>
				<SummaryBanner offsetRight={showVerticalScrollbar ? 16 : 0} />
				{children}
			</Box>
			<Box
				gridArea={'1 / 3 / 3 / 3'}
				role={'scrollbar'}
				display={'block'}
				height={'100%'}
				position={'absolute'}
				width={16}
				zIndex={5}
				sx={{ right: showVerticalScrollbar ? 0 : -16, transition: 'all 0.2s ease' }}
			>
				<Box
					top={0}
					bottom={0}
					width={'100%'}
					position={'absolute'}
					sx={{ cursor: 'pointer' }}
					onClick={handleVerticalTrackClick}
					ref={scrollTrackVerticalRef}
					bgcolor={(theme) => theme.palette[getNightOrDayPalette(theme)][0]}
				/>
				<Box
					height={verticalThumbHeight}
					width={'50%'}
					margin={'0 auto'}
					left={'50%'}
					sx={{ transform: 'translateX(-50%)', opacity: isVerticalDragging ? 1 : 0.75, ':hover': { opacity: 1 } }}
					bgcolor={(theme) => theme.palette[getNightOrDayPalette(theme)][400]}
					borderRadius={4}
					position={'absolute'}
					onMouseDown={handleVerticalThumbDown}
					ref={scrollThumbVerticalRef}
				/>
			</Box>
			<Box gridArea={'3 / 1 / 3 / 3'} role={'scrollbar'} display={'block'} height={'100%'} position={'relative'} width={'100%'}>
				<Stack
					direction={'row'}
					flexWrap={'nowrap'}
					justifyContent={'flex-start'}
					left={0}
					right={0}
					height={'100%'}
					position={'absolute'}
					sx={{ cursor: 'pointer' }}
					onClick={handleHorizontalTrackClick}
					ref={scrollTrackHorizontalRef}
					paddingRight={0.75} // to show the last item shadow, otherwise its hidden by browser border
				>
					{[...content.keys()].map((key, index) => (
						<Stack
							direction={'row'}
							gap={0}
							key={key}
							height={16}
							flex={1}
							alignItems={'flex-end'}
							boxShadow={(theme) =>
								index === 0
									? `inset -1px 0px 0 0px ${theme.palette[getNightOrDayPalette(theme)][500]}, inset 1px 0px 0 0px ${theme.palette[getNightOrDayPalette(theme)][500]}`
									: `1px 0px 0 0px ${theme.palette[getNightOrDayPalette(theme)][500]}`
							}
						>
							{[...new Array(content.get(key)?.acitvitiesAmount)].map((_, index, arr) => (
								<Box
									key={index}
									flex={1}
									height={7}
									boxShadow={
										(theme) => (index + 1 !== arr.length ? `1px 0px 0 0px ${theme.palette[getNightOrDayPalette(theme)][400]}` : 'none') // hide the last shadow as it overlaps with next phase
									}
								/>
							))}
						</Stack>
					))}
				</Stack>
				<Box
					width={horizontalThumbWidth}
					bgcolor={(theme) => theme.palette[getNightOrDayPalette(theme)][400]}
					height={'50%'}
					position={'absolute'}
					top={'50%'}
					borderRadius={4}
					onMouseDown={handleHorizontalThumbDown}
					sx={{ transform: 'translateY(-50%)', opacity: isHorizontalDragging ? 1 : 0.75, ':hover': { opacity: 1 } }}
					ref={scrollThumbHorizontalRef}
				/>
			</Box>
		</Box>
	);
};
